import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    setDistance,
    setUniformElevationScale,
    setChartHeight,
    setPoiFontSize,
    setShowGrid,
    setElevationPointsDensity,
    setSideBySideLayout,
    setOffRouteThreshold,
    setShowPoiDescriptions,
    setShowDistanceToNextPoi,
    setHideOffRoutePoi,
} from '../chartSettings/ChartSettingsSlice';
import { removeFromCache } from '../routeSelection/RouteSelectionSlice';
import { cacheRoute, loadGPXFile } from '../charts/ChartsSlice';
import { useOnline } from '../../context/OnlineContext';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Slider from '@mui/material/Slider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import HelpOutline from '@mui/icons-material/HelpOutline';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Snackbar from '@mui/material/Snackbar';

function ChartSettings() {
    const dispatch = useDispatch();
    const chartSettings = useSelector(state => state.chartSettings.chartSettings);
    const trackPointsWithPoi = useSelector(state => state.charts.chartData);
    const routeData = useSelector(state => state.charts.route_json);
    const gpxError = useSelector(state => state.charts.gpxError);
    const isOnline = useOnline();
    const fileInputRef = useRef(null);

    // Calculate total route distance and remaining miles
    const totalRouteDistance = trackPointsWithPoi.length > 0
        ? trackPointsWithPoi[trackPointsWithPoi.length - 1].d * chartSettings.distMultiplier
        : 0;

    // Parse chunk distances and calculate remaining miles
    const parseChunkDistances = (input) => {
        if (!input || input === '') return [];
        // Handle both string and number inputs
        const inputStr = typeof input === 'string' ? input : input.toString();
        return inputStr.split(',').map(s => s.trim()).filter(s => s !== '').map(s => parseFloat(s)).filter(n => !isNaN(n));
    };

    const chunkDistances = parseChunkDistances(chartSettings.distance);
    const totalChunkedDistance = chunkDistances.reduce((sum, dist) => sum + dist, 0);
    const remainingMiles = totalRouteDistance - totalChunkedDistance;
    const hasVariableChunks = chunkDistances.length > 1;
    const hasError = remainingMiles < 0;

    const handleUpdateRoute = (routeId) => {
        if (routeData?.source === 'gpx') {
            // For GPX files, prompt for new file
            fileInputRef.current?.click();
        } else {
            // For other sources, cache/update the route
            const source = routeData?.source || 'rwgps';
            dispatch(cacheRoute({ routeId, source }));
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                const reader = new FileReader();
                reader.onload = async (event) => {
                    const gpxData = event.target.result;
                    await dispatch(loadGPXFile({ gpxData, fileName: file.name })).unwrap();
                };
                reader.readAsText(file);
            } catch (error) {
                console.error('Error loading GPX file:', error);
            } finally {
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        }
    };

    const handleRemoveFromCache = (routeId) => {
        dispatch(removeFromCache(routeId));
    };

    const InfoTooltip = ({ title }) => (
        <Tooltip title={title} enterDelay={0} enterTouchDelay={0}>
            <IconButton size="small" sx={{ ml: 1, p: 0 }}>
                <HelpOutline fontSize="small" />
            </IconButton>
        </Tooltip>
    );

    return (
        <Box sx={{
            flex: '1 1 auto',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Box sx={{ p: 2, overflow: 'hidden' }}>
                {/* Download/Clear buttons - only show if we have route data */}
                {routeData && (
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6">Download Status</Typography>
                        </Box>
                        <input
                            type="file"
                            accept=".gpx"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleUpdateRoute(routeData.id);
                                }}
                                disabled={routeData?.source !== 'gpx' && !isOnline}
                                sx={{
                                    minWidth: 'auto',
                                    px: 1.5,
                                    py: 0.5,
                                    fontSize: '0.75rem',
                                    height: '32px'
                                }}
                            >
                                {routeData?.isCached === true ? 'Update Route' : 'Save Offline'}
                            </Button>

                            {routeData?.isCached && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveFromCache(routeData.id);
                                    }}
                                    sx={{
                                        minWidth: 'auto',
                                        px: 1.5,
                                        py: 0.5,
                                        fontSize: '0.75rem',
                                        height: '32px'
                                    }}
                                >
                                    Clear
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {routeData?.isCached && routeData?.cachedAt && (
                                <Typography variant="body2" color="text.secondary">
                                    Saved: {(new Date(routeData.cachedAt)).toLocaleString()}
                                </Typography>
                            )}
                            {routeData?.source && routeData?.source !== 'gpx' && (
                                <Link
                                    href={routeData.source === 'strava' ? `https://www.strava.com/routes/${routeData.id}` : `https://ridewithgps.com/routes/${routeData.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                        fontSize: '0.875rem',
                                        fontWeight: routeData.source === 'strava' ? 600 : 400,
                                        color: routeData.source === 'strava' ? '#FC5200' : 'primary.main',
                                        textDecoration: 'underline',
                                        '&:hover': {
                                            textDecoration: 'underline'
                                        }
                                    }}
                                >
                                    {routeData.source === 'strava' ? 'View on Strava' : 'View on RWGPS'}
                                </Link>
                            )}
                            {routeData?.source === 'gpx' && (
                                <Box
                                    sx={{
                                        px: 0.5,
                                        py: 0.25,
                                        fontSize: '0.6rem',
                                        fontWeight: 600,
                                        color: '#666',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '4px',
                                        border: '1px solid',
                                        borderColor: '#ddd',
                                        display: 'inline-block',
                                        width: 'fit-content'
                                    }}
                                >
                                    GPX
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Chart Display</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography>{`Chunk Distance (${chartSettings.distAbbr})`}</Typography>
                    <InfoTooltip title={`Set the distance in ${chartSettings.distAbbr}s for each chunk of the route. You can enter a single number for uniform chunks (e.g., "15") or a comma-separated list for variable chunks (e.g., "15,15,22"). Smaller chunks create more detailed sections.`} />
                </Box>
                <TextField
                    fullWidth
                    id="standard-basic"
                    variant="standard"
                    value={chartSettings.distance || ''}
                    onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                            dispatch(setDistance(''));
                        } else {
                            // Allow comma-separated values
                            dispatch(setDistance(value));
                        }
                    }}
                    sx={{ mb: 2 }}
                />

                {/* Show information about chunk configuration */}
                {hasVariableChunks && (
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="info" sx={{ mb: 1 }}>
                            Using non-uniform chunk lengths: {chunkDistances.map((dist, i) => `${dist}${i < chunkDistances.length - 1 ? ', ' : ''}`).join('')} {chartSettings.distAbbr}
                        </Alert>
                        {remainingMiles > 0 && (
                            <Alert severity="success">
                                {remainingMiles.toFixed(1)} {chartSettings.distAbbr} remaining for last chunk
                            </Alert>
                        )}
                        {hasError && (
                            <Alert severity="warning">
                                Total chunk distance ({totalChunkedDistance.toFixed(1)} {chartSettings.distAbbr}) exceeds route length ({totalRouteDistance.toFixed(1)} {chartSettings.distAbbr}). Excess chunks will be removed during processing.
                            </Alert>
                        )}
                    </Box>
                )}

                {/* Show uniform chunk info */}
                {!hasVariableChunks && chunkDistances.length === 1 && chunkDistances[0] > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="info">
                            Using uniform chunk length: {chunkDistances[0]} {chartSettings.distAbbr}
                        </Alert>
                    </Box>
                )}



                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={chartSettings.showPoiDescriptions}
                                onChange={(e) => dispatch(setShowPoiDescriptions(e.target.checked))}
                            />
                        }
                        label="Show POI Descriptions"
                    />
                    <InfoTooltip title="Display additional descriptive text for Points of Interest" />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={chartSettings.showDistanceToNextPoi}
                                onChange={(e) => dispatch(setShowDistanceToNextPoi(e.target.checked))}
                            />
                        }
                        label="Show Distance to Next POI"
                    />
                    <InfoTooltip title="Display the distance to the next Point of Interest" />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={chartSettings.hideOffRoutePoi}
                                onChange={(e) => dispatch(setHideOffRoutePoi(e.target.checked))}
                            />
                        }
                        label="Hide Off-Route POI"
                    />
                    <InfoTooltip title="Hide all POIs that are further from the route than the off-route threshold" />
                </Box>

                <Box sx={{ mb: 2, px: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography>Off-Route Distance Threshold ({chartSettings.distAbbr})</Typography>
                        <InfoTooltip title={`Set the maximum distance (in ${chartSettings.distAbbr}) that a POI can be from the route before being marked as off-route`} />
                    </Box>
                    <Slider
                        value={chartSettings.offRouteThreshold}
                        min={0.1}
                        max={1.0}
                        step={0.05}
                        onChange={(e, value) => {
                            dispatch(setOffRouteThreshold(value));
                        }}
                        marks={[
                            { value: 0.1, label: '0.1' },
                            { value: 0.25, label: '0.25' },
                            { value: 0.5, label: '0.5' },
                            { value: 1.0, label: '1.0' }
                        ]}
                        valueLabelDisplay="auto"
                    />
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography>POI Font Size</Typography>
                        <InfoTooltip title="Adjust the text size of POI (Points of Interest) labels" />
                    </Box>
                    <Slider
                        value={chartSettings.poiFontSize}
                        min={0.7}
                        max={1.2}
                        step={0.1}
                        onChange={(e, value) => dispatch(setPoiFontSize(value))}
                        marks
                        valueLabelDisplay="auto"
                    />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Chart Layout</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={chartSettings.uniformElevationScale}
                                onChange={(e) => dispatch(setUniformElevationScale(e.target.checked))}
                            />
                        }
                        label="Uniform Elevation Scale"
                    />
                    <InfoTooltip title="When enabled, all charts will use the same elevation scale for better comparison between sections" />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={chartSettings.sideBySideLayout}
                                onChange={(e) => dispatch(setSideBySideLayout(e.target.checked))}
                            />
                        }
                        label="Side by Side Layout"
                    />
                    <InfoTooltip title="Display charts and POI information side by side instead of vertically stacked" />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={chartSettings.showGrid}
                                onChange={(e) => dispatch(setShowGrid(e.target.checked))}
                            />
                        }
                        label="Show Grid"
                    />
                    <InfoTooltip title="Show grid lines on the elevation charts for better readability" />
                </Box>

                <Box sx={{ mb: 2, px: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography>Elevation Points Density</Typography>
                        <InfoTooltip title="Control how many elevation points are shown on the chart. More points show more detail but may increase visual complexity" />
                    </Box>
                    <Slider
                        value={chartSettings.elevationPointsDensity}
                        min={1}
                        max={3}
                        step={1}
                        onChange={(e, value) => {
                            const newHeight = 1.5 + (value - 1) * 0.75;
                            dispatch(setChartHeight(newHeight));
                            dispatch(setElevationPointsDensity(value));
                        }}
                        marks={[
                            { value: 1, label: 'Fewer' },
                            { value: 2, label: 'More' },
                            { value: 3, label: 'Most' }
                        ]}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => {
                            switch (value) {
                                case 1: return 'Fewer Points';
                                case 2: return 'More Points';
                                case 3: return 'All Points';
                                default: return '';
                            }
                        }}
                    />
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography>Chart Height (inches)</Typography>
                        <InfoTooltip title="Adjust the vertical height of each chart section in inches" />
                    </Box>
                    <Slider
                        value={chartSettings.chartHeight}
                        min={1.5}
                        max={3}
                        step={0.1}
                        onChange={(e, value) => {
                            const newDensity = Math.round(1 + (value - 1.5) / 0.75);
                            dispatch(setElevationPointsDensity(newDensity));
                            dispatch(setChartHeight(value));
                        }}
                        marks
                        valueLabelDisplay="auto"
                    />
                </Box>




            </Box>
            <Snackbar
                open={!!gpxError}
                autoHideDuration={6000}
            >
                <Alert severity="error" sx={{ width: '100%' }}>
                    {gpxError}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default ChartSettings; 